import { useState, useEffect } from "react";
import {
  createFileRoute,
  useParams,
  useNavigate,
} from "@tanstack/react-router";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";

import { ProtectedRoute } from "@/components/protected-route";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Label } from "@/components/ui/label";
import JobTasksList from "@/components/tasks/list";

import { selectJobOverviewData, selectTasksByJobId, selectCheckinByBooking } from "@/store/selectors";
import { addJobState, updateJobState } from "@/store/actions";
import {
  updateHelpModal,
  updateNotesModal,
} from "@/store/slices/appStateSlice";
import type { RootState } from "@/store";

export const Route = createFileRoute("/job/$jobId/$date/protective-photos")({
  component: () => (
    <ProtectedRoute>
      <JobProtectivePhotosPage />
    </ProtectedRoute>
  ),
});

function JobProtectivePhotosPage() {
  const { jobId, date } = useParams({ from: "/job/$jobId/$date/protective-photos" });
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get job data from Redux
  const { booking, job, jobState } = useSelector(
    (state: RootState) => selectJobOverviewData(state, jobId)
  );

  const checkinToday = useSelector((state: RootState) =>
    booking ? selectCheckinByBooking(state, booking.id, booking.date) : null
  );

  // Get tasks for this job
  const tasks = useSelector((state: RootState) =>
    selectTasksByJobId(state, jobId)
  );

  // Check if protective photos are already confirmed
  useEffect(() => {
    if (jobState?.confirmed_protective_photos) {
      // Redirect to photos if already confirmed
      navigate({ to: "/job/$jobId/$date/photos", params: { jobId, date } });
    }
  }, [jobId]);

  const handleGoBack = () => {
    navigate({ to: "/job/$jobId/$date", params: { jobId, date } });
  };

  const handleBeginWork = () => {
    if (!job) return;

    const timestamp = moment().format("YYYY-MM-DD HH:mm:ss");

    if (jobState) {
      dispatch(
        updateJobState({
          ...jobState,
          confirmed_protective_photos: timestamp,
        })
      );
    } else {
      dispatch(
        addJobState({
          job_id: job.id,
          confirmed_protective_photos: timestamp,
        })
      );
    }

    // Navigate to photos after confirming protective photos
    navigate({
      to: "/job/$jobId/$date",
      params: { jobId, date },
      search: (old) => ({
        ...old,
        canStart: true,
      }),
    });
  };

  const showNotesModal = () => {
    dispatch(
      updateNotesModal({
        show: true,
        canAssignTask: true,
      })
    );
  };

  const showHelpModal = () => {
    dispatch(updateHelpModal(true));
  };

  if (!job || !booking) {
    return (
      <div className="col-span-2 flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-600">Job not found</h2>
          <p className="text-gray-500">Could not load job data</p>
        </div>
      </div>
    );
  }

  return (
    <ScrollArea className="col-span-2 h-full overflow-y-auto overflow-x-hidden max-h-[calc(100vh-5rem)]">
      <div className="p-8 flex justify-center">
        <div className="max-w-4xl w-full space-y-8">
          <div className="space-y-4">
            <h1 className="text-3xl font-bold leading-tight m-0">
              Please provide photos of your protective areas.
            </h1>

            <p className="text-base leading-relaxed mt-4 mb-4">
              Before you commence with any work, you must supply evidence that
              you have sufficiently protected your surroundings.
            </p>
          </div>

          <div className="space-y-4">
            <Label className="text-gray-800 font-semibold text-sm uppercase">
              Tasks
            </Label>

            <JobTasksList
              tasks={tasks}
              canAddPhotos={true}
              canUpdateTimesheet={true}
              updateJobStatus={true}
              checkin={checkinToday}
            />
          </div>

          <div className="flex gap-4 py-8 border-t border-gray-200">
            <Button onClick={handleBeginWork} className="flex-1">
              Begin Work
            </Button>

            <Button
              variant="secondary"
              onClick={showHelpModal}
              className="flex-1"
            >
              I Need Assistance
            </Button>
          </div>

          <div className="flex gap-4 pb-8">
            <Button variant="outline" onClick={handleGoBack} className="flex-1">
              Return to Overview
            </Button>

            <Button
              variant="outline"
              onClick={showNotesModal}
              className="flex-1"
            >
              Add Note to Job
            </Button>
          </div>
        </div>
      </div>
    </ScrollArea>
  );
}
